import React, { useState, useEffect } from "react";
import SummaryApi from "../common";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const daysOfWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const timeOptions = [
  "06:00:00", "07:00:00", "08:00:00", "09:00:00", "10:00:00", "11:00:00",
  "12:00:00", "13:00:00", "14:00:00", "15:00:00", "16:00:00", "17:00:00",
  "18:00:00", "19:00:00", "20:00:00", "21:00:00",
];

const weekOptions = [
  { value: 6, label: "6 Weeks" },
  { value: 12, label: "12 Weeks" }
];

const AddInstructorCourseSchedule = () => {
  const [selectedDays, setSelectedDays] = useState([]);
  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [instructorName, setInstructorName] = useState("");
  const [timings, setTimings] = useState({});
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [batchName, setBatchName] = useState("");
  const [selectedWeek, setSelectedWeek] = useState("");
  const [existingSchedules, setExistingSchedules] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const navigate = useNavigate();

  // Theme handling based on localStorage - FIXED: Use "theme" instead of "admin-theme"
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    const shouldBeDark = savedTheme === "dark";
    setIsDarkMode(shouldBeDark);
    
    // Apply theme to document for global CSS support
    if (shouldBeDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, []);

  // Listen for theme changes from other components - FIXED: Use "theme" instead of "admin-theme"
  useEffect(() => {
    const handleThemeChange = () => {
      const savedTheme = localStorage.getItem("theme");
      const shouldBeDark = savedTheme === "dark";
      setIsDarkMode(shouldBeDark);
      
      // Apply theme to document
      if (shouldBeDark) {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    };

    window.addEventListener("themeChanged", handleThemeChange);
    
    // Listen for storage changes (other tabs)
    const handleStorageChange = (e) => {
      if (e.key === "theme") {
        handleThemeChange();
      }
    };
    
    window.addEventListener("storage", handleStorageChange);

    // Listen for custom theme toggle events
    const handleCustomThemeChange = () => {
      handleThemeChange();
    };
    
    window.addEventListener("adminThemeToggled", handleCustomThemeChange);
    
    return () => {
      window.removeEventListener("themeChanged", handleThemeChange);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("adminThemeToggled", handleCustomThemeChange);
    };
  }, []);

  useEffect(() => {
    const instructorId = sessionStorage.getItem("instructor_id");

    if (instructorId) {
      // Fetch courses with categories
      fetch(SummaryApi.getCourseIdByInstructorId.url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ instructor_id: Number(instructorId) }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status && Array.isArray(data.data)) {
            const categoryMap = new Map();
            const uniqueCategories = [];

            data.data.forEach((item) => {
              if (!categoryMap.has(item.category_id)) {
                categoryMap.set(item.category_id, item.category_name);
                uniqueCategories.push({
                  category_id: item.category_id,
                  category_name: item.category_name,
                });
              }
            });

            setCategories(uniqueCategories);
            setCourses(data.data);
          } else {
            console.error("Failed to fetch courses:", data);
          }
        })
        .catch((err) => console.error("Error fetching courses:", err));

      // Fetch instructor name
      fetch(SummaryApi.instructorDetailsByInstructorId.url, {
        method: SummaryApi.instructorDetailsByInstructorId.method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ instructor_id: Number(instructorId) }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status && data.data?.instructor_name) {
            setInstructorName(data.data.instructor_name);
          }
        })
        .catch((err) => console.error("Error fetching instructor name:", err));
    }
  }, []);

  // Calculate end date based on week selection
  useEffect(() => {
    if (startDate && selectedWeek) {
      const start = new Date(startDate);
      const end = new Date(start);
      end.setDate(start.getDate() + (selectedWeek * 7));
      setEndDate(end.toISOString().split('T')[0]);
    }
  }, [startDate, selectedWeek]);

  // Fetch existing schedules when dates are selected
  useEffect(() => {
    const instructorId = sessionStorage.getItem("instructor_id");
    
    if (instructorId && startDate && endDate) {
      fetch(SummaryApi.instructorTimeSlotInfo.url, {
        method: SummaryApi.instructorTimeSlotInfo.method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          instructor_id: Number(instructorId),
          start_date: startDate,
          end_date: endDate
        }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status && Array.isArray(data.data)) {
            setExistingSchedules(data.data);
          } else {
            setExistingSchedules([]);
          }
        })
        .catch((err) => {
          console.error("Error fetching existing schedules:", err);
          setExistingSchedules([]);
        });
    } else {
      setExistingSchedules([]);
    }
  }, [startDate, endDate]);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategoryId(categoryId);
    const filtered = courses.filter(
      (c) => String(c.category_id) === String(categoryId)
    );
    setFilteredCourses(filtered);
    setSelectedCourseId(""); // Reset course on category change
  };

  const toggleDay = (day) => {
    if (selectedDays.includes(day)) {
      setSelectedDays(prev => prev.filter(d => d !== day));
      // Remove timings for deselected day
      setTimings(prev => {
        const newTimings = { ...prev };
        delete newTimings[day];
        return newTimings;
      });
    } else {
      if (selectedDays.length < 2) {
        setSelectedDays(prev => [...prev, day]);
      } else {
        Swal.fire({
          icon: "warning",
          title: "Limit Reached", 
          text: "You can only select up to 2 days.",
          background: isDarkMode ? '#1f2937' : '#fff',
          color: isDarkMode ? '#f9fafb' : '#000',
        });
      }
    }
  };

  const checkTimeConflict = (day, startTime, endTime) => {
    if (!existingSchedules.length) return null;

    const dayLower = day.toLowerCase();
    
    for (const schedule of existingSchedules) {
      const existingTimeSlot = schedule[dayLower];
      
      if (existingTimeSlot && existingTimeSlot !== "N/A" && existingTimeSlot !== "0") {
        const [existingStart, existingEnd] = existingTimeSlot.split('-');
        
        // Check if the new time overlaps with existing time
        if (
          (startTime >= existingStart && startTime < existingEnd) ||
          (endTime > existingStart && endTime <= existingEnd) ||
          (startTime <= existingStart && endTime >= existingEnd)
        ) {
          return {
            courseId: schedule.course_id,
            batchName: schedule.batch_name,
            day: day,
            existingStart: existingStart.slice(0, 5),
            existingEnd: existingEnd.slice(0, 5),
            dayTime: existingTimeSlot
          };
        }
      }
    }
    
    return null;
  };

  const handleTimeChange = async (day, type, value) => {
    const currentTiming = timings[day] || {};
    const newStartTime = type === "start" ? value : currentTiming.start;
    const newEndTime = type === "end" ? value : currentTiming.end;

    setTimings((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [type]: value,
      },
    }));

    // 🔹 Only check when user selects a start time
    if (type === "start" && newStartTime && startDate && endDate) {
      const instructorId = Number(sessionStorage.getItem("instructor_id"));

      try {
        const res = await fetch(SummaryApi.instructorTimeSlotInfo.url, {
          method: SummaryApi.instructorTimeSlotInfo.method,
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            instructor_id: instructorId,
            start_date: startDate,
            end_date: endDate,
          }),
        });

        const data = await res.json();

        if (data.status && Array.isArray(data.data)) {
          setExistingSchedules(data.data);

          // 🔹 Check if the selected start time conflicts with any existing slot
          const dayLower = day.toLowerCase();
          for (const schedule of data.data) {
            const existingTimeSlot = schedule[dayLower];
            if (existingTimeSlot && existingTimeSlot !== "N/A" && existingTimeSlot !== "0") {
              const [existingStart, existingEnd] = existingTimeSlot.split("-");
              if (newStartTime >= existingStart && newStartTime < existingEnd) {
                Swal.fire({
                  icon: "warning",
                  title: "Time Slot Conflict",
                  html: `This start time overlaps with your existing schedule:<br>
                         <strong>Course:</strong>${schedule.course_title}<br>
                         <strong>Batch:</strong> ${schedule.batch_name}<br>
                         <strong>Day:</strong> ${day}<br>
                         <strong>Time:</strong> ${existingStart.slice(0,5)} - ${existingEnd.slice(0,5)}`,
                  confirmButtonText: "OK",
                  background: isDarkMode ? '#1f2937' : '#fff',
                  color: isDarkMode ? '#f9fafb' : '#000',
                });

                // 🔹 Reset conflicting start time to "0" to block reuse
                setTimings((prev) => ({
                  ...prev,
                  [day]: { ...prev[day], start: "0" },
                }));
                break;
              }
            }
          }
        } else {
          setExistingSchedules([]);
        }
      } catch (err) {
        console.error("Error checking start time conflict:", err);
      }
    }

    // 🔹 Check conflict when selecting end time too
    if (type === "end" && newEndTime && startDate && endDate) {
      const instructorId = Number(sessionStorage.getItem("instructor_id"));

      try {
        const res = await fetch(SummaryApi.instructorTimeSlotInfo.url, {
          method: SummaryApi.instructorTimeSlotInfo.method,
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            instructor_id: instructorId,
            start_date: startDate,
            end_date: endDate,
          }),
        });

        const data = await res.json();

        if (data.status && Array.isArray(data.data)) {
          const dayLower = day.toLowerCase();
          for (const schedule of data.data) {
            const existingTimeSlot = schedule[dayLower];
            if (existingTimeSlot && existingTimeSlot !== "N/A" && existingTimeSlot !== "0") {
              const [existingStart, existingEnd] = existingTimeSlot.split("-");
              if (newEndTime > existingStart && newEndTime <= existingEnd) {
                Swal.fire({
                  icon: "warning",
                  title: "Time Slot Conflict",
                  html: `This end time overlaps with your existing schedule:<br>
                         <strong>Course:</strong> Batch ${schedule.batch_name}<br>
                         <strong>Day:</strong> ${day}<br>
                         <strong>Time:</strong> ${existingStart.slice(0,5)} - ${existingEnd.slice(0,5)}`,
                  confirmButtonText: "OK",
                  background: isDarkMode ? '#1f2937' : '#fff',
                  color: isDarkMode ? '#f9fafb' : '#000',
                });

                // 🔹 Reset conflicting end time to "0" to block reuse
                setTimings((prev) => ({
                  ...prev,
                  [day]: { ...prev[day], end: "0" },
                }));
                break;
              }
            }
          }
        }
      } catch (err) {
        console.error("Error checking end time conflict:", err);
      }
    }
  };

  const handleSaveTimings = async () => {
    const instructorId = Number(sessionStorage.getItem("instructor_id"));

    if (!instructorId || !selectedCourseId) {
      Swal.fire({
        icon: "warning",
        title: "Missing Info", 
        text: "Please select a course.",
        background: isDarkMode ? '#1f2937' : '#fff',
        color: isDarkMode ? '#f9fafb' : '#000',
      });
      return;
    }

    // Check for conflicts before saving
    for (let day of selectedDays) {
      const { start = "", end = "" } = timings[day] || {};
      if (!start || !end) {
        Swal.fire({
          icon: "warning",
          title: "Incomplete Timing",
          text: `Set time for ${day}.`,
          background: isDarkMode ? '#1f2937' : '#fff',
          color: isDarkMode ? '#f9fafb' : '#000',
        });
        return;
      }
      if (start >= end) {
        Swal.fire({
          icon: "error",
          title: "Invalid Timing",
          text: `Start must be before end on ${day}.`,
          background: isDarkMode ? '#1f2937' : '#fff',
          color: isDarkMode ? '#f9fafb' : '#000',
        });
        return;
      }
      
      // Final conflict check before saving
      const conflict = checkTimeConflict(day, start, end);
      if (conflict) {
        Swal.fire({
          icon: "error",
          title: "Cannot Save - Time Conflict",
          html: `This time slot conflicts with your existing schedule:<br>
                 <strong>Course:</strong> Batch ${conflict.batchName}<br>
                 <strong>Day:</strong> ${conflict.day}<br>
                 <strong>Time:</strong> ${conflict.existingStart} - ${conflict.existingEnd}<br><br>
                 Please choose a different time slot.`,
          confirmButtonText: "OK",
          background: isDarkMode ? '#1f2937' : '#fff',
          color: isDarkMode ? '#f9fafb' : '#000',
        });
        return;
      }
    }

    if (!startDate || !endDate) {
      Swal.fire({
        icon: "warning",
        title: "Missing Dates",
        text: "Select start date and week duration.",
        background: isDarkMode ? '#1f2937' : '#fff',
        color: isDarkMode ? '#f9fafb' : '#000',
      });
      return;
    }

    if (new Date(endDate) < new Date(startDate)) {
      Swal.fire({
        icon: "error",
        title: "Invalid Dates",
        text: "End date must be after start date.",
        background: isDarkMode ? '#1f2937' : '#fff',
        color: isDarkMode ? '#f9fafb' : '#000',
      });
      return;
    }

    if (!batchName.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Batch Name",
        text: "Enter a batch name.",
        background: isDarkMode ? '#1f2937' : '#fff',
        color: isDarkMode ? '#f9fafb' : '#000',
      });
      return;
    }

    if (selectedDays.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Days Selected",
        text: "Please select at least one day.",
        background: isDarkMode ? '#1f2937' : '#fff',
        color: isDarkMode ? '#f9fafb' : '#000',
      });
      return;
    }

    // Prepare data for single insert with all days
    const timeSlotData = {
      instructor_id: instructorId,
      course_id: Number(selectedCourseId),
      batch_name: batchName.trim(),
      start_date: startDate,
      end_date: endDate,
      is_course_completed: 0,
      is_active: 1,
      created_date: new Date().toISOString().split('T')[0]
    };

    // Add day-specific time slots
    daysOfWeek.forEach(day => {
      const dayLower = day.toLowerCase();
      if (selectedDays.includes(day) && timings[day]) {
        timeSlotData[dayLower] = `${timings[day].start}-${timings[day].end}`;
      } else {
        timeSlotData[dayLower] = "N/A";
      }
    });

    try {
      const res = await fetch(SummaryApi.createInstructorTimeslot.url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(timeSlotData),
      });

      const data = await res.json();

      if (data.status) {
        Swal.fire({
          icon: "success",
          title: "Saved!",
          text: "Course schedule saved successfully.",
          timer: 2000,
          showConfirmButton: false,
          background: isDarkMode ? '#1f2937' : '#fff',
          color: isDarkMode ? '#f9fafb' : '#000',
        }).then(() => {
          navigate("/instructor-panel/instructorCourseSchedule");
        });
      } else {
        console.error("Failed to save course schedule", data);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to save course schedule.",
          background: isDarkMode ? '#1f2937' : '#fff',
          color: isDarkMode ? '#f9fafb' : '#000',
        });
      }
    } catch (error) {
      console.error("Save error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save course schedule.",
        background: isDarkMode ? '#1f2937' : '#fff',
        color: isDarkMode ? '#f9fafb' : '#000',
      });
    }
  };

  // Theme-aware CSS classes
  const containerClass = `w-full mx-auto border rounded-xl shadow-lg overflow-hidden transition-colors duration-300 ${
    isDarkMode ? 'bg-gray-900 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'
  }`;

  const headerClass = `p-4 flex justify-between items-center transition-colors duration-300 ${
    isDarkMode ? 'bg-blue-900 text-white' : 'bg-blue-600 text-white'
  }`;

  const sectionClass = `transition-colors duration-300 ${
    isDarkMode ? 'bg-gray-900' : 'bg-white'
  }`;

  const inputClass = `border rounded px-3 py-3 transition-colors duration-300 ${
    isDarkMode 
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400' 
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
  }`;

  const selectClass = `border rounded px-2 py-1 w-full transition-colors duration-300 ${
    isDarkMode 
      ? 'bg-gray-700 border-gray-600 text-white' 
      : 'bg-white border-gray-300 text-gray-900'
  }`;

  const tableHeaderClass = `text-sm text-left transition-colors duration-300 ${
    isDarkMode 
      ? 'bg-gray-800 text-white border-gray-700' 
      : 'bg-gray-100 text-gray-900 border-gray-300'
  }`;

  const tableRowClass = `text-sm border-b transition-colors duration-300 ${
    isDarkMode 
      ? 'bg-gray-800 border-gray-700' 
      : 'bg-white border-gray-300'
  }`;

  const tableCellClass = `p-3 border transition-colors duration-300 ${
    isDarkMode 
      ? 'border-gray-700 text-white' 
      : 'border-gray-300 text-gray-900'
  }`;

  const buttonPrimaryClass = `px-4 py-3 rounded transition-colors duration-300 ${
    isDarkMode 
      ? 'bg-[#1C3664] text-white hover:bg-blue-700' 
      : 'bg-[#1C3664] text-white hover:bg-blue-700'
  }`;

  const buttonSecondaryClass = `px-4 py-3 rounded transition-colors duration-300 ${
    isDarkMode 
      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
  }`;

  const labelClass = `text-sm font-medium mb-1 transition-colors duration-300 ${
    isDarkMode ? 'text-white' : 'text-gray-700'
  }`;
return (
  <div className={`${containerClass} max-w-7xl mx-auto`}>
<div className={` flex justify-between py-3 px-4 bg-[#1C3664]`}>
      <h2 className="text-lg text-white font-semibold">Add Course Schedule</h2>
      <a 
        href="/instructor-panel/instructorCourseSchedule" 
        className="text-white hover:text-gray-200 hover:underline transition-colors duration-300 text-sm lg:text-base"
      >
        My Schedule
      </a>
    </div>

{/* Top Section – ONE ROW on large screens */}
<div className={`p-4 lg:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 lg:gap-6 ${sectionClass}`}>
  {/* Categories */}
  <div className="flex flex-col">
    <label className={labelClass}>Categories</label>
    <select
      className={`${inputClass} w-full`}
      value={selectedCategoryId}
      onChange={(e) => handleCategoryChange(e.target.value)}
    >
      <option value="">Select Category</option>
      {categories.map((category) => (
        <option key={category.category_id} value={category.category_id}>
          {category.category_name}
        </option>
      ))}
    </select>
  </div>

  {/* Course */}
  <div className="flex flex-col">
    <label className={labelClass}>Course</label>
    <select
      className={`${inputClass} w-full`}
      value={selectedCourseId}
      onChange={(e) => setSelectedCourseId(e.target.value)}
      disabled={!selectedCategoryId}
    >
      <option value="">
        {selectedCategoryId ? "Select Course" : "Select category first"}
      </option>
      {selectedCategoryId &&
        filteredCourses.map((course) => (
          <option key={course.course_id} value={course.course_id}>
            {course.course_title}
          </option>
        ))}
    </select>
  </div>

  {/* Start Date */}
  <div className="flex flex-col">
    <label className={labelClass}>Start Date</label>
    <input
      type="date"
      className={`${inputClass} w-full`}
      value={startDate}
      onChange={(e) => setStartDate(e.target.value)}
    />
  </div>

  {/* Weeks */}
  <div className="flex flex-col">
    <label className={labelClass}>Weeks</label>
    <select
      className={`${inputClass} w-full`}
      value={selectedWeek}
      onChange={(e) => setSelectedWeek(e.target.value)}
    >
      <option value="">Select Weeks</option>
      {weekOptions.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>

  {/* End Date */}
  <div className="flex flex-col">
    <label className={labelClass}>End Date</label>
    <input
      type="date"
      className={`w-full border rounded px-3 py-3 transition-colors duration-300 ${
        isDarkMode 
          ? 'bg-gray-700 border-gray-600 text-gray-300' 
          : 'bg-gray-100 border-gray-300 text-gray-500'
      }`}
      value={endDate}
      readOnly
    />
  </div>

  {/* Batch Name */}
  <div className="flex flex-col">
    <label className={labelClass}>Batch Name</label>
    <input
      type="text"
      className={`${inputClass} w-full`}
      placeholder="Enter batch name"
      value={batchName}
      onChange={(e) => setBatchName(e.target.value)}
    />
  </div>
</div>

    {/* Table - Full width, clean spacing on large screens */}
    <div className={`overflow-x-auto ${sectionClass}`}>
      <div className="min-w-full inline-block align-middle">
        <table className="min-w-full border-t">
          <thead>
            <tr className={tableHeaderClass}>
              <th className={`${tableCellClass} text-left text-sm lg:text-base`}>Day</th>
              <th className={`${tableCellClass} text-left text-sm lg:text-base`}>Tutor</th>
              <th className={`${tableCellClass} text-left text-sm lg:text-base`}>Start Time</th>
              <th className={`${tableCellClass} text-left text-sm lg:text-base`}>End Time</th>
            </tr>
          </thead>
          <tbody>
            {daysOfWeek.map((day) => {
              const isDisabled = selectedDays.length >= 2 && !selectedDays.includes(day);
              return (
                <tr key={day} className={tableRowClass}>
                  <td className={`${tableCellClass} font-medium`}>
                    <label className="inline-flex items-center gap-3">
                      <input
                        type="checkbox"
                        className={`form-checkbox h-4 w-4 transition-colors duration-300 ${
                          isDarkMode 
                            ? 'text-blue-400 bg-gray-700 border-gray-600' 
                            : 'text-blue-600 bg-white border-gray-300'
                        }`}
                        checked={selectedDays.includes(day)}
                        onChange={() => toggleDay(day)}
                        disabled={isDisabled}
                      />
                      <span className="text-sm lg:text-base">{day}</span>
                      {isDisabled && (
                        <span className={`text-sm lg:text-sm ml-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          (Max 2 days)
                        </span>
                      )}
                    </label>
                  </td>
                  <td className={`${tableCellClass} text-sm lg:text-base`}>{instructorName || "Instructor"}</td>
                  <td className={tableCellClass}>
                    <select
                      className={`${selectClass} w-full text-sm lg:text-base`}
                      value={timings[day]?.start || ""}
                      onChange={(e) => handleTimeChange(day, "start", e.target.value)}
                      disabled={!selectedDays.includes(day)}
                    >
                      <option value="">Start Time</option>
                      {timeOptions.map((time) => {
                        const endTime = timings[day]?.end || "";
                        return (
                          <option key={time} value={time} disabled={endTime && time >= endTime}>
                            {time.slice(0, 5)}
                          </option>
                        );
                      })}
                    </select>
                  </td>
                  <td className={tableCellClass}>
                    <select
                      className={`${selectClass} w-full text-sm lg:text-base`}
                      value={timings[day]?.end || ""}
                      onChange={(e) => handleTimeChange(day, "end", e.target.value)}
                      disabled={!selectedDays.includes(day)}
                    >
                      <option value="">End Time</option>
                      {timeOptions.map((time) => {
                        const startTime = timings[day]?.start || "";
                        return (
                          <option key={time} value={time} disabled={startTime && time <= startTime}>
                            {time.slice(0, 5)}
                          </option>
                        );
                      })}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>

    {/* Buttons - Better spacing on large screens */}
    <div className={`p-6 flex justify-start gap-6 ${sectionClass}`}>
      <button
        className={`${buttonPrimaryClass} px-8 py-3 text-base`}
        onClick={handleSaveTimings}
      >
        Save Timings
      </button>
      <button className={`${buttonSecondaryClass} px-8 py-3 text-base`}>
        Cancel
      </button>
    </div>
  </div>
);
};

export default AddInstructorCourseSchedule;